import { Controller, useForm } from "react-hook-form";

import Button from "@components/Button";
import Modal, { ModalFooter, ModalHeader, ModalTitle } from "@components/Modal";
import { i18n } from "@lingui/core";

import { AESimpleDatepicker } from "./AESimpleDatepicker";

export interface QueryFieldErrors {
  [field: string]: string | string[] | undefined;
}

export interface ComponentErrors {
  queries?: QueryFieldErrors[];
  non_field_errors?: string[];
  title?: string;
  search_type?: string;
  start_date?: string;
  end_date?: string;
  sorting_position?: string;
  [extraKey: string]: unknown;
}
type OptionTypeLabels = Record<string, string>;

interface DetailsModalProps {
  show: boolean;
  onHide: () => void;
  onClose: () => void;

  title: string;
  setTitle: (value: string) => void;

  startDate: Date | null | undefined;
  endDate: Date | null | undefined;
  setStartDate: (value: Date | null) => void;
  setEndDate: (value: Date | null) => void;

  searchType: string;
  setSearchType: (value: string) => void;
  optionTypeLabels: OptionTypeLabels;

  sortingPosition: number | string;
  setSortingPosition: (value: string) => void;

  errors: ComponentErrors;

  onSave: () => void;
  isSaving: boolean;
}

interface FormData {
  title: string;
  startDate: Date | null;
  endDate: Date | null;
  searchType: string;
  sortingPosition: string;
}

const DetailsModal: React.FC<DetailsModalProps> = (props) => {
  const { control, handleSubmit, watch } = useForm<FormData>({
    values: {
      title: props.title,
      startDate: props.startDate || null,
      endDate: props.endDate || null,
      searchType: props.searchType,
      sortingPosition: String(props.sortingPosition),
    },
  });

  const watchedStartDate = watch("startDate");

  const onSubmit = (data: FormData) => {
    props.setTitle(data.title);
    props.setStartDate(data.startDate);
    props.setEndDate(data.endDate);
    props.setSearchType(data.searchType);
    props.setSortingPosition(data.sortingPosition);
    props.onSave();
  };

  const errors = props.errors;

  return (
    <Modal show={props.show} onHide={props.onHide}>
      <ModalHeader>
        <ModalTitle>{L.MENTIONS_QUERYBUILDER_MODAL_TITLE}</ModalTitle>
      </ModalHeader>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-3">
          <label htmlFor="formGroupTitle">
            {L.MENTIONS_QUERYBUILDER_TITLE_LABEL}
          </label>
          <Controller
            name="title"
            control={control}
            rules={{ required: "Title is required" }}
            render={({ field }) => (
              <input
                {...field}
                id="formGroupTitle"
                type="text"
                className={`form-control p-2 m-1 block border-gray-200 border rounded ${errors.title ? "is-invalid" : ""}`}
                placeholder={L.MENTIONS_QUERYBUILDER_TITLE_PLACEHOLDER}
                autoComplete="off"
                onBlur={(e) => {
                  field.onBlur();
                  props.setTitle(e.target.value);
                }}
              />
            )}
          />
          {errors.title && (
            <div className="invalid-feedback block">{errors.title}</div>
          )}
        </div>

        <div className="mb-3" data-testid="save-search-select-dates">
          <label htmlFor="formGroupStartDate" className="block">
            {L.MENTIONS_QUERYBUILDER_START_DATE_LABEL}
          </label>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <AESimpleDatepicker
                className={`form-control ${errors.start_date ? "is-invalid" : ""}`}
                dateFormat="yyyy-MM-dd"
                onChange={(date) => {
                  field.onChange(date);
                  props.setStartDate(date);
                }}
                placeholderText={L.MENTIONS_QUERYBUILDER_START_DATE_PLACEHOLDER}
                popperPlacement="bottom-start"
                selected={field.value}
                wrapperClassName="flex"
              />
            )}
          />
          {errors.start_date && (
            <div className="invalid-feedback block">{errors.start_date}</div>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="formGroupEndDate" className="block">
            {L.MENTIONS_QUERYBUILDER_END_DATE_LABEL}
          </label>
          <Controller
            name="endDate"
            control={control}
            render={({ field }) => (
              <AESimpleDatepicker
                selected={field.value}
                onChange={(date) => {
                  field.onChange(date);
                  props.setEndDate(date);
                }}
                dateFormat="yyyy-MM-dd"
                className={`form-control ${errors.end_date ? "is-invalid" : ""}`}
                popperPlacement="bottom-start"
                wrapperClassName="flex"
                minDate={watchedStartDate}
                isClearable
                clearButtonClassName="clear-button"
                placeholderText={L.MENTIONS_QUERYBUILDER_END_DATE_PLACEHOLDER}
              />
            )}
          />
          {errors.end_date && (
            <div className="invalid-feedback block">{errors.end_date}</div>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="formGroupType">
            {L.MENTIONS_QUERYBUILDER_MODAL_TYPE_LABEL}
          </label>
          <Controller
            name="searchType"
            control={control}
            rules={{ required: "Search type is required" }}
            render={({ field }) => (
              <select
                {...field}
                id="formGroupType"
                className={`form-control p-2 m-1 block border-gray-200 border rounded ${errors.search_type ? "is-invalid" : ""}`}
                onChange={(e) => {
                  field.onChange(e);
                  props.setSearchType(e.target.value);
                }}
                required
              >
                <option value="" disabled>
                  {L.MENTIONS_QUERYBUILDER_MODAL_TYPE_PLACEHOLDER}
                </option>
                {Object.entries(props.optionTypeLabels).map(
                  ([value, label]) => (
                    <option key={value} value={value}>
                      {i18n._(label)}
                    </option>
                  )
                )}
              </select>
            )}
          />
          {errors.search_type && (
            <div className="invalid-feedback block">
              {L.MENTIONS_QUERYBUILDER_SEARCH_TYPE_ERROR}
            </div>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="formGroupSortingPosition">
            {L.MENTIONS_QUERYBUILDER_FILTER_SORTING_POSITION}
          </label>
          <Controller
            name="sortingPosition"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                id="formGroupSortingPosition"
                type="number"
                className={`form-control p-2 m-1 block border-gray-200 border rounded ${errors.sorting_position ? "is-invalid" : ""}`}
                onChange={(e) => {
                  field.onChange(e);
                  props.setSortingPosition(e.target.value);
                }}
              />
            )}
          />
          <small className="text-muted-foreground">
            {L.MENTIONS_QUERYBUILDER_FILTER_SORTING_POSITION_HELP}
          </small>
          {errors.sorting_position && (
            <div className="invalid-feedback block">
              {errors.sorting_position}
            </div>
          )}
        </div>
      </form>

      <ModalFooter>
        <Button variant="secondary" onClick={props.onClose}>
          {L.MENTIONS_QUERYBUILDER_MODAL_CLOSE}
        </Button>

        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={props.isSaving}
          data-testid="save-alert-button"
        >
          {L.MENTIONS_QUERYBUILDER_SAVE_BUTTON}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DetailsModal;
